import { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { ZoomIn, ZoomOut, Move, Check, X } from 'lucide-react';

interface AvatarCropperProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageSrc: string;
  onCropComplete: (croppedBlob: Blob) => void;
}

export const AvatarCropper = ({ open, onOpenChange, imageSrc, onCropComplete }: AvatarCropperProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);

  const CANVAS_SIZE = 280;
  const OUTPUT_SIZE = 400; // Final cropped image size

  // Load image
  useEffect(() => {
    if (!imageSrc || !open) return;
    
    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      setImageLoaded(true);
      
      // Calculate initial zoom to fit image in canvas
      const scale = Math.max(CANVAS_SIZE / img.width, CANVAS_SIZE / img.height);
      setZoom(scale * 1.2); // Slightly larger for cropping flexibility
      setPosition({ x: 0, y: 0 });
    };
    img.src = imageSrc;
    
    return () => {
      setImageLoaded(false);
    };
  }, [imageSrc, open]);

  // Draw image on canvas
  const drawImage = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const img = imageRef.current;
    
    if (!canvas || !ctx || !img || !imageLoaded) return;

    // Clear canvas
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    
    // Draw circular clipping path
    ctx.save();
    ctx.beginPath();
    ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, CANVAS_SIZE / 2, 0, Math.PI * 2);
    ctx.clip();
    
    // Calculate image dimensions and position
    const scaledWidth = img.width * zoom;
    const scaledHeight = img.height * zoom;
    const x = (CANVAS_SIZE - scaledWidth) / 2 + position.x;
    const y = (CANVAS_SIZE - scaledHeight) / 2 + position.y;
    
    // Draw image
    ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
    ctx.restore();
    
    // Draw circular border
    ctx.beginPath();
    ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, CANVAS_SIZE / 2 - 2, 0, Math.PI * 2);
    ctx.strokeStyle = 'hsl(var(--border))';
    ctx.lineWidth = 3;
    ctx.stroke();
  }, [zoom, position, imageLoaded]);

  useEffect(() => {
    drawImage();
  }, [drawImage]);

  // Handle mouse/touch events for dragging
  const handlePointerDown = (e: React.PointerEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    // Limit position to keep image covering the circle
    const img = imageRef.current;
    if (img) {
      const scaledWidth = img.width * zoom;
      const scaledHeight = img.height * zoom;
      const maxOffsetX = Math.max(0, (scaledWidth - CANVAS_SIZE) / 2);
      const maxOffsetY = Math.max(0, (scaledHeight - CANVAS_SIZE) / 2);
      
      setPosition({
        x: Math.max(-maxOffsetX, Math.min(maxOffsetX, newX)),
        y: Math.max(-maxOffsetY, Math.min(maxOffsetY, newY))
      });
    }
  };

  const handlePointerUp = () => {
    setIsDragging(false);
  };

  // Generate cropped image
  const handleCrop = () => {
    const img = imageRef.current;
    if (!img) return;

    // Create high-resolution output canvas
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = OUTPUT_SIZE;
    outputCanvas.height = OUTPUT_SIZE;
    const ctx = outputCanvas.getContext('2d');
    
    if (!ctx) return;

    // Draw circular clipping path
    ctx.beginPath();
    ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
    ctx.clip();

    // Calculate scaled dimensions for output
    const scale = OUTPUT_SIZE / CANVAS_SIZE;
    const scaledWidth = img.width * zoom * scale;
    const scaledHeight = img.height * zoom * scale;
    const x = (OUTPUT_SIZE - scaledWidth) / 2 + position.x * scale;
    const y = (OUTPUT_SIZE - scaledHeight) / 2 + position.y * scale;

    // Draw image
    ctx.drawImage(img, x, y, scaledWidth, scaledHeight);

    // Convert to blob
    outputCanvas.toBlob((blob) => {
      if (blob) {
        onCropComplete(blob);
        onOpenChange(false);
      }
    }, 'image/png', 1.0);
  };

  const handleZoomChange = (value: number[]) => {
    setZoom(value[0]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Move className="h-4 w-4" />
            Bild anpassen
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col items-center gap-4 py-4">
          {/* Canvas for cropping */}
          <div 
            ref={containerRef}
            className="relative cursor-move touch-none select-none"
            style={{ width: CANVAS_SIZE, height: CANVAS_SIZE }}
          >
            <canvas
              ref={canvasRef}
              width={CANVAS_SIZE}
              height={CANVAS_SIZE}
              className="rounded-full bg-muted"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
            />
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            )}
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Ziehe das Bild um es zu positionieren
          </p>

          {/* Zoom control */}
          <div className="w-full flex items-center gap-3 px-4">
            <ZoomOut className="h-4 w-4 text-muted-foreground" />
            <Slider
              value={[zoom]}
              onValueChange={handleZoomChange}
              min={0.5}
              max={3}
              step={0.05}
              className="flex-1"
            />
            <ZoomIn className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4 mr-2" />
            Abbrechen
          </Button>
          <Button onClick={handleCrop} disabled={!imageLoaded}>
            <Check className="h-4 w-4 mr-2" />
            Übernehmen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
